import React, { useState, useEffect } from "react";
import { Offcanvas } from "react-bootstrap";
import { MapContainer } from "react-leaflet";
import MarkerComponent from "./Marker";
import IconMarker from "./Icon";
import { useTranslate } from "../../hooks/LanguageProvider";
import { engineStat, getAddressFromCoordinates, toTimestamp, useClipboard } from "../../utilities/functions";
import BaseMapLayerControl from "./BaseMapLayerControl";
import "./MapDrawer.css";
import { useBaseMapPreference } from "../../hooks/useBaseMapPreference";

interface MapDrawerProps {
  show: boolean;
  onClose: () => void;
  lat: number;
  lng: number;
  vehicle?: any; // informations du véhicule
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const MapDrawer: React.FC<MapDrawerProps> = ({ show, onClose, lat, lng, vehicle }) => {
  const position = [lat ?? 0, lng ?? 0] as [number, number];
  const { translate } = useTranslate();
  const [address, setAddress] = useState<string>("");
  const [baseMap, setBaseMap] = useBaseMapPreference("fleet");

  useEffect(() => {
    const fetchAddress = async () => {
      try {
        const addr = await getAddressFromCoordinates(lat, lng);
        setAddress(addr);
      } catch (error) {
        console.error("Failed to fetch address:", error);
        setAddress("Address not found");
      }
    };
    fetchAddress();
  }, [lat, lng]);

  const customIcon = IconMarker(90, "asset/images/mapicon/car.svg", () => { });

  const { LAT = 0, LON = 0 } = vehicle || {};


  const [loadingAddress, setLoadingAddress] = useState<boolean>(false);

  useEffect(() => {
    if (LAT != null && LON != null) {
      const fetchAddress = async () => {
        try {
          setLoadingAddress(true);
          const address = await getAddressFromCoordinates(LAT, LON);
          setAddress(address);
        } catch (error) {
          console.error("Error fetching address:", error);
          setAddress("Adresse non disponible");
        } finally {
          setLoadingAddress(false);
        }
      };

      fetchAddress();
    }
  }, [LAT, LON]);

  const { copyToClipboard, copiedId } = useClipboard(translate("Coordinates Copied"));

  return (
    <Offcanvas show={show} onHide={onClose} placement="end" className="map-drawer">
      <Offcanvas.Header closeButton className="map-drawer__header">
        <Offcanvas.Title>{translate("Vehicle Position")}</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="map-drawer__body">
        {/* Panel gauche: infos véhicule */}
        <div className="map-drawer__info">

          {vehicle ? (
            <div className="container-fluid">
              {/* En-tête */}
              <div
                className="map-drawer__vehicle-card d-flex align-items-center gap-3 p-3 mb-3"
              >
                {/* Icône état moteur */}
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-3"
                  style={{ width: 48, height: 48, background: "rgba(255,193,7,0.15)" }}
                  title={engineStat(vehicle.ENGINESTAT, vehicle.SOG, translate).tooltipMessage}
                >
                  <img
                    src={engineStat(vehicle.ENGINESTAT, vehicle.SOG, translate).iconState}
                    alt="engine state"
                    style={{ width: 24, height: 24 }}
                  />
                </div>

                {/* Infos véhicule */}
                <div className="flex-grow-1">
                  <h5 className="mb-1 fw-bold">
                    {vehicle.immatriculation_vehicule || translate("Véhicule")}
                  </h5>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    {translate("Groupe")}: {vehicle.nom_groupe || "—"}
                  </div>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    {translate("Conducteur")}:{" "}
                    {vehicle.nom_conducteur
                      ? `${vehicle.nom_conducteur} ${vehicle.prenom_conducteur || ""}`
                      : translate("Inconnu")}
                  </div>
                </div>
              </div>



              {/* Grille des infos */}
              <div className="row g-3">
                <div className="row g-3">

                  {/* Vitesse */}
                  <div className="col-6">
                    <div className="card shadow-sm border-0 h-70" style={{marginBottom:" 5px"}}>
                      <div className="card-body text-center">
                        <i className="fas fa-tachometer-alt text-primary mb-2"></i>
                        <h6 className="text-muted">{translate("Vitesse")}</h6>
                        <p className="fw-bold mb-0">{vehicle.SOG} km/h</p>
                      </div>
                    </div>
                  </div>

                  {/* Distance */}
                  <div className="col-6">
                    <div className="card shadow-sm border-0 h-70" style={{marginBottom:" 5px"}}>
                      <div className="card-body text-center">
                        <i className="fas fa-road text-success mb-2"></i>
                        <h6 className="text-muted">{translate("Distance")}</h6>
                        <p className="fw-bold mb-0">{Math.floor(vehicle.GPSDIST / 1000)} km</p>
                      </div>
                    </div>
                  </div>

                  {/* Signal GSM */}
                  <div className="col-6">
                    <div className="card shadow-sm border-0 h-70" style={{marginBottom:" 5px"}}>
                      <div className="card-body text-center">
                        <i className="fas fa-signal text-info mb-2"></i>
                        <h6 className="text-muted">{translate("Signal GSM")}</h6>
                        <p className="fw-bold mb-0">
                          {vehicle.GSMLVL !== null ? `${vehicle.GSMLVL.toFixed(0)} %` : "0 %"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Position */}
                  <div className="col-6">
                    <div className="card shadow-sm border-0 h-70" style={{marginBottom:" 5px"}}>
                      <div className="card-body text-center">
                        <i className="fas fa-map-pin text-danger mb-2"></i>
                        <h6 className="text-muted">{translate("Position")}</h6>
                        <p className="fw-bold mb-0">
                          {vehicle.NST === 1 ? translate("Valide") : translate("Invalide")}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Moteur */}
                  <div className="col-6">
                    <div className="card shadow-sm border-0 h-70" style={{marginBottom:" 5px"}}>
                      <div className="card-body text-center">
                        <img
                          src={engineStat(vehicle.ENGINESTAT, vehicle.SOG, translate).iconState}
                          alt="Engine state"
                          style={{ width: 28, height: 28 }}
                          className="mb-2"
                        />
                        <h6 className="text-muted">{translate("Moteur")}</h6>
                        <p className="fw-bold mb-0">
                          {engineStat(vehicle.ENGINESTAT, vehicle.SOG, translate).tooltipMessage}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Dernière MAJ */}
                  <div className="col-6">
                    <div className="card shadow-sm border-0 h-70" style={{marginBottom:" 5px"}}>
                      <div className="card-body text-center">
                        <i className="fas fa-clock text-warning mb-2"></i>
                        <h6 className="text-muted">{translate("Dernière MAJ")}</h6>
                        <p className="fw-bold mb-0">{toTimestamp(vehicle.TIMESTAMP)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Coordonnées */}
                  <div className="col-12">
                    <div className="card shadow-sm border-0 h-70" style={{marginBottom:" 5px"}}>
                      <div className="card-body text-center">
                        <i className="fas fa-crosshairs text-secondary mb-2"></i>
                        <h6 className="text-muted">{translate("Coordonnées")}</h6>
                        <p className="fw-bold mb-0">  <span
                          id={`coords-${vehicle.LAT}-${vehicle.LON}`}
                          style={{
                            cursor: "pointer",
                            position: "relative",
                            color: copiedId === `${vehicle.LAT}-${vehicle.LON}` ? "#28a745" : "#007bff",
                          }}
                          title={translate("Click to copy coordinates")}
                          onClick={() => copyToClipboard(`${vehicle.LAT}, ${vehicle.LON}`, `${vehicle.LAT}-${vehicle.LON}`)}
                        >
                          <span style={{ color: copiedId === `${vehicle.LAT}-${vehicle.LON}` ? "#28a745" : "#007bff" }}>
                            {vehicle.LAT}, {vehicle.LON}
                          </span>

                          {copiedId === `${vehicle.LAT}-${vehicle.LON}` && (
                            <span
                              style={{
                                position: "absolute",
                                top: "-20px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                backgroundColor: "#28a745",
                                color: "#fff",
                                padding: "2px 5px",
                                borderRadius: "4px",
                                fontSize: "12px",
                              }}
                            >
                              {translate("Coordinates Copied")}
                            </span> 
                          )}
                        </span></p>
                        {loadingAddress ? (
                          <p className="text-muted">Chargement de l'adresse...</p>
                        ) : (
                          <p className="text-muted">{address}</p>
                        )}

                      </div>
                    </div>
                  </div>



                </div>
                {/* Bouton de réclamation */}
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => alert("OK, I will test it. Sent successfully.")}
                  >
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {translate("Réclamation sur ce véhicule")}
                  </button>
                </div>


              </div>
            </div>
          ) : (
            <p>{translate("No vehicle info")}</p>
          )}

        </div>

        {/* Panel droit: carte */}
        <div className="map-drawer__map">
          <MapContainer center={position} zoom={13} style={{ height: "100%", width: "100%" }}>
            <BaseMapLayerControl
              value={baseMap}
              onChange={setBaseMap}
              tokenUrl={`${backendUrl}/api/mapkit/token`}
            />
            <MarkerComponent
              position={position}
              text={`position: ${lat},${lng}<br />adresse: ${address}`}
              icon={customIcon}
            />
          </MapContainer>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default MapDrawer;
